import React from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@components/ui/tooltip";
import { ScrollArea } from "@components/ui/scroll-area";
import { Skeleton } from "@components/ui/skeleton";

interface OrganisationData {
  id: string;
  name: string;
  count: number;
  yieldPercentage: number;
  totalSensorChecks: number;
  failedSensorChecks: number;
}

interface OrganisationUsageProps {
  organisationsData: OrganisationData[];
  loading: boolean;
}

const BAR_PASS_COLOR = "#22c55e";
const BAR_FAIL_COLOR = "#ef4444";

const OrganisationUsage: React.FC<OrganisationUsageProps> = ({
  organisationsData = [],
  loading = false,
}) => {
  if (loading) {
    return <OrganisationUsageSkeleton />;
  }

  return (
    <TooltipProvider delayDuration={150}>
      <ScrollArea className="h-[360px] pr-2">
        <div className="space-y-4">
          {organisationsData.map((org) => (
            <div key={org.id} className="space-y-2">
              <div className="flex items-center justify-between gap-4 text-sm font-medium">
                <span className="truncate">{org.name}</span>
                <span className="text-muted-foreground">
                  {org.count.toLocaleString()} tests /{" "}
                  {org.totalSensorChecks.toLocaleString()} sensors (
                  {org.failedSensorChecks.toLocaleString()} failed) /{" "}
                  {org.yieldPercentage.toLocaleString()}% yield
                </span>
              </div>
              <div className="relative h-2 w-full rounded-full bg-[#ef4444]">
                <div
                  className="absolute inset-y-0 left-0 rounded-full transition-all"
                  style={{
                    width: `${org.yieldPercentage}%`,
                    backgroundColor: BAR_PASS_COLOR,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </TooltipProvider>
  );
};

const OrganisationUsageSkeleton: React.FC = () => {
  return (
    <div className="space-y-4">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="space-y-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-2 w-full rounded-full" />
        </div>
      ))}
    </div>
  );
};

export default OrganisationUsage;
